import { createSlice, createAsyncThunk } from "@reduxjs/toolkit"
import { get } from "../../lib/request"
import {
  appendOrganizationBrandIds,
  appendOrganizationClientIds,
  organizationClientIdsKey,
} from "../../lib/utils/organizationReportClientIds"

export const ORGANIZATION_PAID_TOP_CAMPAIGNS_LIMIT = 5

function topCampaignsKey({ organizationId, startDate, endDate, limit, clientIds, brandIds } = {}) {
  return `${String(organizationId ?? "")}|${String(startDate ?? "")}|${String(endDate ?? "")}|${String(limit ?? "")}|${organizationClientIdsKey(clientIds)}|${organizationClientIdsKey(brandIds)}`
}

/**
 * GET /api/v1/report/organizations/{organizationId}/paid/top-campaigns?start_date=&end_date=&limit=&client_ids=&brand_ids=
 */
export const fetchOrganizationPaidTopCampaigns = createAsyncThunk(
  "organizationPaidTopCampaigns/fetch",
  async (
    {
      organizationId,
      startDate,
      endDate,
      limit = ORGANIZATION_PAID_TOP_CAMPAIGNS_LIMIT,
      clientIds,
      brandIds,
    } = {},
    { rejectWithValue },
  ) => {
    try {
      if (organizationId == null || organizationId === "") {
        return rejectWithValue("organizationId is required")
      }
      if (!startDate || !endDate) {
        return rejectWithValue("start_date and end_date are required")
      }
      if (limit == null || limit === "") {
        return rejectWithValue("limit is required")
      }

      const query = new URLSearchParams({
        start_date: startDate,
        end_date: endDate,
        limit: String(limit),
      })
      appendOrganizationClientIds(query, clientIds)
      appendOrganizationBrandIds(query, brandIds)
      const data = await get(`report/organizations/${organizationId}/paid/top-campaigns?${query}`)

      return {
        data,
        organizationId,
        startDate,
        endDate,
        limit,
        fetchKey: topCampaignsKey({ organizationId, startDate, endDate, limit, clientIds, brandIds }),
      }
    } catch (err) {
      return rejectWithValue(err?.message || "Failed to fetch organization paid top campaigns")
    }
  },
  {
    condition: (arg, { getState }) => {
      if (arg?.force) return true
      const key = topCampaignsKey({
        ...arg,
        limit: arg?.limit ?? ORGANIZATION_PAID_TOP_CAMPAIGNS_LIMIT,
      })
      const state = getState().organizationPaidTopCampaigns
      if (state?.status === "loading" && state?.requestedFetchKey === key) return false
      if (state?.status === "succeeded" && state?.fetchedFetchKey === key) return false
      return true
    },
  },
)

const initialState = {
  data: null,
  status: "idle",
  error: null,
  organizationId: null,
  startDate: null,
  endDate: null,
  limit: null,
  requestedFetchKey: null,
  fetchedFetchKey: null,
}

const organizationPaidTopCampaignsSlice = createSlice({
  name: "organizationPaidTopCampaigns",
  initialState,
  reducers: {
    clearOrganizationPaidTopCampaigns: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchOrganizationPaidTopCampaigns.pending, (state, action) => {
        const arg = action.meta.arg || {}
        state.status = "loading"
        state.error = null
        state.requestedFetchKey = topCampaignsKey({
          ...arg,
          limit: arg.limit ?? ORGANIZATION_PAID_TOP_CAMPAIGNS_LIMIT,
        })
      })
      .addCase(fetchOrganizationPaidTopCampaigns.fulfilled, (state, action) => {
        if (action.payload?.fetchKey !== state.requestedFetchKey) return
        state.status = "succeeded"
        state.data = action.payload?.data ?? null
        state.organizationId = action.payload?.organizationId ?? null
        state.startDate = action.payload?.startDate ?? null
        state.endDate = action.payload?.endDate ?? null
        state.limit = action.payload?.limit ?? null
        state.fetchedFetchKey = action.payload?.fetchKey ?? null
      })
      .addCase(fetchOrganizationPaidTopCampaigns.rejected, (state, action) => {
        const arg = action.meta.arg || {}
        if (
          topCampaignsKey({
            ...arg,
            limit: arg.limit ?? ORGANIZATION_PAID_TOP_CAMPAIGNS_LIMIT,
          }) !== String(state.requestedFetchKey ?? "")
        ) {
          return
        }
        state.status = "failed"
        state.error = action.payload ?? "Failed to fetch organization paid top campaigns"
        state.data = null
        state.fetchedFetchKey = state.requestedFetchKey
      })
  },
})

export const { clearOrganizationPaidTopCampaigns } = organizationPaidTopCampaignsSlice.actions
export default organizationPaidTopCampaignsSlice.reducer
